import { motion } from "framer-motion";
import QuoteModal from "@/components/QuoteModal";
import reclaimedFireplace from "@/assets/reclaimed-project-fireplace.webp";
import reclaimedVent from "@/assets/reclaimed-project-vent.webp";
import reclaimedCloseup from "@/assets/reclaimed-project-closeup.webp";
import reclaimedCeilingWide from "@/assets/reclaimed-ceiling-wide.webp";
import reclaimedCeilingVaulted from "@/assets/reclaimed-ceiling-vaulted.webp";
import reclaimedCeilingDetail from "@/assets/reclaimed-ceiling-detail.webp";

const floorProjects = [
  { img: reclaimedFireplace, alt: "Reclaimed antique oak floor in random 4 to 8 inch widths with an oil finish at a brick hearth, Lexington, MI", title: "Reclaimed Antique Oak & Brick Hearth", location: "Lexington, Michigan" },
  { img: reclaimedVent, alt: "Close up of a flush brass honeycomb register in reclaimed antique oak, Lexington, MI", title: "Brass Honeycomb Flush-Mount Register", location: "Lexington, Michigan" },
  { img: reclaimedCloseup, alt: "Close up of original saw marks on a reclaimed antique oak floor, Lexington, MI", title: "Antique Oak  Original Saw Mark Patina", location: "Lexington, Michigan" },
];

const ceilingProjects = [
  { img: reclaimedCeilingWide, alt: "Thermally modified reclaimed Douglas fir 8 inch plank ceiling, Lexington, MI", title: "Antique Douglas Fir Vaulted Ceiling", location: "Lexington, Michigan" },
  { img: reclaimedCeilingVaulted, alt: "Reclaimed Douglas fir cathedral ceiling with a fabricated beam, Lexington, MI", title: "Cathedral Ceiling & Fabricated Beam", location: "Lexington, Michigan" },
  { img: reclaimedCeilingDetail, alt: "Detail of a thermally modified reclaimed Douglas fir ceiling, Lexington, MI", title: "Thermally Modified Fir Detail", location: "Lexington, Michigan" },
];

const ReclaimedProjectsGallery = () => (
  <section className="bg-background py-14 md:py-20 lg:py-28" id="gallery">
    <div className="max-w-[1200px] mx-auto px-5 md:px-12">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="flex flex-col md:flex-row md:items-end md:justify-between mb-8 md:mb-10"
      >
        <div>
          <p className="text-[11px] font-semibold tracking-[.18em] uppercase text-primary mb-3">
            Current Project
          </p>
          <h2 className="font-display text-[clamp(28px,5vw,52px)] font-semibold text-foreground leading-[1.1]">
            Work in <em className="italic font-normal text-primary">progress.</em>
          </h2>
        </div>
        <QuoteModal
          trigger={
            <button className="mt-4 md:mt-0 inline-flex items-center gap-2 border border-border text-foreground px-6 py-3 rounded-full text-xs font-medium hover:border-primary/30 hover:shadow-md transition-all self-start md:self-auto">
              Discuss Your Project →
            </button>
          }
        />
      </motion.div>

      {/* Floor Projects Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {floorProjects.map((p, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.06 }}
            className="relative overflow-hidden rounded-md aspect-[4/5] group"
          >
            <img width={800} height={534} loading="lazy"
              src={p.img}
              alt={p.alt}
               className="absolute inset-0 w-full h-full object-cover transition-all duration-700 ease-out grayscale-[20%] contrast-[1.1] group-hover:grayscale-0 group-hover:scale-[1.04]"
            />
            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-foreground/80 via-foreground/40 to-transparent p-4 pt-12 md:p-5 md:pt-16">
              <h3 className="font-display text-sm md:text-base font-semibold text-primary-foreground leading-tight">{p.title}</h3>
              <p className="text-[10px] md:text-xs tracking-[.12em] uppercase text-primary-foreground/70 mt-1">{p.location}</p>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Ceiling Projects Header */}
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="mt-14 md:mt-20 mb-8 md:mb-10"
      >
        <p className="text-[11px] font-semibold tracking-[.18em] uppercase text-primary mb-3">
          Reclaimed Ceilings &amp; Beams
        </p>
        <h2 className="font-display text-[clamp(24px,4vw,44px)] font-semibold text-foreground leading-[1.1]">
          Antique Douglas Fir  <em className="italic font-normal text-primary">thermally modified.</em>
        </h2>
        <p className="mt-3 text-[13px] md:text-[15px] font-light text-muted-foreground leading-relaxed max-w-xl">
          8-inch wide plank reclaimed Douglas Fir ceiling with a fabricated beam. Carefully thermally modified and installed by our artisan team.
        </p>
      </motion.div>

      {/* Ceiling Projects Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {ceilingProjects.map((p, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.06 }}
            className="relative overflow-hidden rounded-md aspect-[4/5] group"
          >
            <img width={800} height={534} loading="lazy"
              src={p.img}
              alt={p.alt}
              className="absolute inset-0 w-full h-full object-cover transition-all duration-700 ease-out grayscale-[20%] contrast-[1.1] group-hover:grayscale-0 group-hover:scale-[1.04]"
            />
            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-foreground/80 via-foreground/40 to-transparent p-4 pt-12 md:p-5 md:pt-16">
              <h3 className="font-display text-sm md:text-base font-semibold text-primary-foreground leading-tight">{p.title}</h3>
              <p className="text-[10px] md:text-xs tracking-[.12em] uppercase text-primary-foreground/70 mt-1">{p.location}</p>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

export default ReclaimedProjectsGallery;
